import { App, Collapse, Form, Input, Modal, Typography } from 'antd';
import { LANGUAGES } from 'constants/languages';
import { FC, memo, useEffect, useState } from 'react';
import { ValidateErrorEntity } from 'rc-field-form/lib/interface';
import { useSearchParams } from 'react-router-dom';
import { fetchFaqById, updateFaq } from 'services/api/faq';
import { FaqDetail, FaqInput } from 'types/api/faq';

import { showErrorMessage } from 'utils/showErrorMessage';
import { getInputDir } from 'utils/helpers';

interface FieldsValue {
  sort_index?: number | null;
  question: {
    [key: string]: string;
  };
  answer: {
    [key: string]: string;
  };
}

interface IProps {
  id: string;
  onClose: () => void;
  maxSortIndex: number;
  onSuccess?: () => void;
}

const UpdateFaq: FC<IProps> = ({ id, onClose, maxSortIndex, onSuccess }) => {
  const { message } = App.useApp();
  const [form] = Form.useForm();
  const [errorKeys, setErrorKeys] = useState<string[]>([]);
  const [searchParams] = useSearchParams();
  const serviceId = searchParams.get('service');

  const [faqIdData, setFaqIdData] = useState<FaqDetail | null>(null);
  const [isUpdating, setIsUpdating] = useState(false);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;

    const load = async () => {
      try {
        const res = await fetchFaqById(id);
        if (!cancelled) setFaqIdData(res.data ?? null);
      } catch {
        // swallow — form stays empty on failure
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [id]);

  const closeModal = () => {
    form.resetFields();
    onClose();
  };

  const onFinish = async (
    values: Record<string, Record<string, string> | string | undefined>
  ) => {
    setErrorKeys([]);
    const input: FaqInput = {
      question: values.question as Record<string, string>,
      answer: values.answer as Record<string, string>,
      sort_index: values.sort_index ? Number(values.sort_index) : null,
      service_id: Number(serviceId) || null
    };

    setIsUpdating(true);

    try {
      await updateFaq(id, input);
      message.success('Successfully updated');
      onSuccess?.();
      closeModal();
    } catch (err) {
      showErrorMessage(err);
    } finally {
      setIsUpdating(false);
    }
  };

  const onFinishError = (
    errorInfo: ValidateErrorEntity<Record<string, string>>
  ) => {
    setErrorKeys([]);
    const errors = new Set(
      errorInfo.errorFields.map(item => `${item.name[0]}`)
    );

    setErrorKeys(Array.from(errors));
  };

  useEffect(() => {
    if (!faqIdData) return;
    const fieldsValue: FieldsValue = {
      sort_index: faqIdData.sort_index ?? null,
      question: {},
      answer: {}
    };

    LANGUAGES.forEach(language => {
      fieldsValue.question[language.locale] =
        faqIdData.question?.[language.locale] || '';
      fieldsValue.answer[language.locale] =
        faqIdData.answer?.[language.locale] || '';
    });

    form.setFieldsValue(fieldsValue);
  }, [faqIdData, form]);

  return (
    <Modal
      open
      onCancel={closeModal}
      confirmLoading={isUpdating}
      onOk={() => form.submit()}
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={onFinish}
        onFinishFailed={onFinishError}
      >
        <Form.Item
          name="sort_index"
          label="Display Order"
          rules={[
            {
              pattern: /^[0-9]+$/,
              message: 'Please enter a valid number'
            },
            {
              validator: (_, value) =>
                value && value > maxSortIndex
                  ? Promise.reject(
                      `Number must be less than or equal to ${maxSortIndex}`
                    )
                  : Promise.resolve()
            }
          ]}
        >
          <Input />
        </Form.Item>
        <Collapse
          accordion
          defaultActiveKey={LANGUAGES[0].locale}
          items={LANGUAGES.map(item => {
            const hasError = errorKeys.includes(item.locale);

            return {
              key: item.locale,
              forceRender: true,
              label: (
                <Typography.Text
                  style={{
                    color: `var(--${hasError ? 'error-color' : 'text-color'})`
                  }}
                >
                  {item.label}
                  {hasError ? ' (error)' : ''}
                </Typography.Text>
              ),
              children: (
                <>
                  <Form.Item
                    name={['question', item.locale]}
                    label="Question"
                    rules={[{ required: true }]}
                  >
                    <Input dir={getInputDir(item.locale)} />
                  </Form.Item>
                  <Form.Item
                    name={['answer', item.locale]}
                    label="Answer"
                    rules={[{ required: true }]}
                  >
                    <Input.TextArea dir={getInputDir(item.locale)} />
                  </Form.Item>
                </>
              )
            };
          })}
        />
      </Form>
    </Modal>
  );
};

export default memo(UpdateFaq);
